import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * Growth Engineer — one of the five open roles the Careers page lists.
 *
 * The copy is Digitopia's own job description, set in this site's section
 * vocabulary rather than as a wall of prose: the mission on the grey band, the
 * four success measures as a list, the responsibilities and the offer as
 * full-width rows. The emoji headings of the source document are dropped — the
 * rest of the site sets its section headings as type, and a page of emoji
 * dividers would be the only one.
 *
 * Every "Apply now" points at `#contact`, the shared contact band's own anchor
 * at the foot of the document, so the button scrolls to the form rather than
 * leaving the page.
 */
export const route: RouteDef = {
  id: 'careersGrowthEngineer',
  path: 'become-a-digitopian/growth-engineer',
  priority: 0.6,
}

export const careersGrowthEngineer: WrittenPage = {
  navLabel: 'Growth Engineer',
  title: 'Growth Engineer',
  description:
    'Open role at Digitopia: Growth Engineer, hybrid in Istanbul. Part builder, part analyst, part commercial problem solver — build the systems behind qualified demand, conversion and expansion.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Careers',
      headline: 'Growth Engineer',
      sub: 'Location: Hybrid (Istanbul) — Full-time',
      body: [
        'Part builder, part analyst, part commercial problem solver: connect data, automation, product experiences and go-to-market teams into repeatable systems for demand, conversion, expansion and learning.',
      ],
      ctas: [{ label: 'Apply now', href: '#apply' }],
    },
    {
      kind: 'prose',
      heading: 'We’re on a mission',
      paragraphs: [
        'Digitopia is reinventing consulting. We help ambitious organizations #ScaleValue. As creators of the Impact Platform and maturity frameworks, we enable companies to understand where they stand, decide what matters next, and turn digital and AI ambition into measurable progress.',
        'Digitopia has a global mission and a strong combination of consulting expertise, proprietary frameworks, benchmark data and AI-powered software. Growth means making that value easier for the right organizations and partners to discover, understand and act on.',
        'To build a more scalable growth engine, we are looking for a Growth Engineer: part builder, part analyst, and part commercial problem solver. You will connect data, automation, product experiences and go-to-market teams to create repeatable systems for qualified demand, conversion, expansion and learning.',
      ],
    },
    {
      kind: 'list',
      heading: 'What success looks like',
      intro:
        'You will help Digitopia grow through systems and evidence, not isolated campaigns. Success means:',
      items: [
        'Increasing qualified demand and conversion across priority markets and customer segments.',
        'Building a trustworthy view of the funnel from first interaction to revenue and expansion.',
        'Running disciplined experiments that generate both growth and learning.',
        'Automating repetitive go-to-market work while protecting relevance, quality and brand trust.',
      ],
    },
    {
      kind: 'features',
      variant: 'rows',
      heading: 'What you’ll do',
      items: [
        {
          title: 'Engineer the growth funnel',
          body: 'Map journeys across website, content, events, partnerships, product experiences, sales and customer expansion; identify where better systems can unlock growth.',
        },
        {
          title: 'Build measurement foundations',
          body: 'Define events, attribution logic, funnel stages, dashboards and data quality checks so teams can make decisions from a shared view.',
        },
        {
          title: 'Run experiments',
          body: 'Design, implement and evaluate tests across acquisition, activation, conversion, engagement and retention with clear hypotheses and success measures.',
        },
        {
          title: 'Create automation',
          body: 'Build workflows for research, segmentation, enrichment, lead routing, personalization, follow-up and reporting using APIs, code, AI and automation tools.',
        },
        {
          title: 'Improve digital experiences',
          body: 'Partner with product and marketing to optimize landing pages, forms, self-assessments, onboarding paths and conversion journeys.',
        },
        {
          title: 'Apply AI responsibly',
          body: 'Use agents and generative AI to accelerate research, content operations and personalization while maintaining human judgment, accuracy and brand quality.',
        },
        {
          title: 'Remove bottlenecks with the teams that feel them',
          body: 'Work with sales, marketing and customer success to identify bottlenecks and build practical solutions.',
        },
        {
          title: 'Turn learning into a system',
          body: 'Document experiments, share insights, and convert repeatable wins into playbooks, reusable tooling and operating routines.',
        },
      ],
    },
    {
      kind: 'list',
      heading: 'What makes you a great fit',
      items: [
        'You bring relevant experience in growth engineering, product growth, marketing operations, revenue operations, product analytics, data, automation or full-stack development.',
        'You are comfortable with SQL and can use Python, JavaScript/TypeScript or comparable tools to connect systems and automate workflows.',
        'You understand funnels, conversion, lifecycle stages, experimentation, and the difference between activity metrics and business outcomes.',
        'You can work with APIs, CRM data, analytics tools, spreadsheets and dashboards without losing sight of the customer experience.',
        'You are commercially curious and want to understand why an organization buys, adopts, expands or disengages.',
        'You can translate an insight into a test, and a successful test into a reliable process or product improvement.',
        'You care about data quality, privacy, relevance and brand trust when using automation and AI.',
        'You communicate clearly and can work across technical, creative and commercial teams.',
        'You move quickly, measure honestly, and are comfortable when an experiment disproves your idea.',
        'You are fluent in English. An additional language is a plus.',
        'Show us what you have built: please include links to relevant experiments, automations, dashboards, products, case studies, GitHub projects or growth results where available.',
      ],
    },
    {
      kind: 'list',
      heading: 'Experience with any of the following would be a plus',
      items: [
        'CRM and automation: HubSpot, Salesforce, n8n, Zapier, Make, or comparable workflow and revenue systems.',
        'Analytics: GA4, Google Tag Manager, Segment, Mixpanel, Amplitude, BI tools, experimentation platforms or data warehouses.',
        'Web and product: landing page development, technical SEO, product onboarding, self-service journeys or conversion optimization.',
        'Commercial context: B2B SaaS, enterprise sales, consulting, partnerships, international growth or account-based go-to-market models.',
      ],
      body: 'You do not need to match every tool. We care more about analytical thinking, technical resourcefulness, commercial curiosity, and your ability to build a repeatable growth system from a real bottleneck.',
    },
    {
      kind: 'features',
      variant: 'rows',
      heading: 'What’s in it for you',
      items: [
        {
          title: 'Build the growth engine',
          body: 'Shape the systems, data and experiments that support Digitopia’s next stage of international growth.',
        },
        {
          title: 'A rare intersection',
          body: 'Work across growth, product, data, automation, AI and enterprise go-to-market.',
        },
        {
          title: 'Fast feedback',
          body: 'See how your work changes funnel performance, team productivity and commercial outcomes.',
        },
        {
          title: 'Global exposure',
          body: 'Support growth across markets, industries, partners and executive audiences.',
        },
        {
          title: 'Continuous growth',
          body: 'Access mentorship, courses, experimentation time and internal knowledge-sharing.',
        },
        {
          title: 'Balanced flexibility',
          body: 'Enjoy hybrid working, flexible hours and generous paid time off.',
        },
        {
          title: 'Supportive culture',
          body: 'Join a team that values authenticity, curiosity, bold ideas and learning through action.',
        },
        {
          title: 'Long-term rewards',
          body: 'Receive a highly competitive compensation package with stock options tied to your lasting impact.',
        },
      ],
    },
    {
      kind: 'prose',
      align: 'center',
      heading:
        'Ready to build a growth system that combines technical leverage, commercial insight and measurable learning?',
      sub: 'Apply now. We would love to meet you if you are ready to experiment intelligently, automate thoughtfully, and build for scale.',
      cta: { label: 'Apply now', href: '#apply' },
    },
    {
      kind: 'apply',
      opening: 'growth-engineer',
      heading: 'Apply for Growth Engineer',
      sub: 'Hybrid (Istanbul), full-time. Tell us who you are and attach your CV; we read every application and reply either way.',
    },
  ],
}
